import React, { useEffect, useRef, useState } from 'react';

/* Placeholders for content that arrives over the network.

   Two problems here that pull against each other, and solving only one makes
   the other worse.

   The first is that a page with nothing on it reads as broken. An article's
   header, prose and contents rail all arrive in the same response, so until it
   lands there is genuinely nothing to draw, and a word like "Loading" set on an
   empty page is barely more informative than the empty page. The placeholders
   below stand in the shape of the thing being fetched, so the layout is already
   settled when the real content replaces it and nothing jumps.

   The second is that on a fast connection the response beats the eye. A
   placeholder shown for ninety milliseconds is a flash, and a flash reads worse
   than the wait it was meant to cover. The usual fix is to delay the
   placeholder so a quick response never shows one, but that leaves the blank
   page this is here to remove. So it goes the other way: once a placeholder is
   shown it stays for a beat, long enough to register as a deliberate state
   rather than a flicker.

   The two together mean the page always shows something, and whatever it shows
   is on screen long enough to have been worth showing. */

/* Long enough to read as intentional, short enough that nobody on a fast line
   feels held up. Below about a quarter of a second the eye reads it as a
   glitch; past about three quarters it starts to feel like the site is slow. */
const FLOOR = 420;

/* Takes the real loading flag and returns one that will not drop inside the
   floor. Everything else about it behaves normally: it goes true the moment
   loading starts, and a fetch that takes longer than the floor is unaffected. */
export const usePlaceholder = (loading, floor = FLOOR) => {

    const startedAt = useRef(Date.now());
    const [held, setHeld] = useState(loading);

    useEffect(() => {

        if(loading) {
            startedAt.current = Date.now();
            setHeld(true);
            return;
        }

        // already past the floor, so there is nothing to wait for
        const elapsed = Date.now() - startedAt.current;
        if(elapsed >= floor) {
            setHeld(false);
            return;
        }

        const timer = window.setTimeout(() => setHeld(false), floor - elapsed);
        return () => window.clearTimeout(timer);

    }, [floor, loading]);

    return held;
}

/* Lines as percentage widths. A block of identical full-width bars reads as a
   loading graphic; uneven ones read as text, which is what is coming. The last
   line of each paragraph is short, the way a last line is. */
const PARAGRAPHS = [
    [98, 100, 96, 100, 61],
    [100, 94, 99, 72],
    [97, 100, 100, 93, 45]
];

const RAIL_ROWS = [86, 64, 92, 71, 58];

const Lines = ({ widths }) => widths.map((width, index) => (
    <span className={'sk-line'} key={index} style={{ width: `${width}%` }} />
));

/* An article, before it arrives. Built on the real grid classes rather than a
   layout of its own, so the placeholder occupies the same space the piece will
   and the swap moves nothing. */
export const ArticleSkeleton = () => {
    return (
        <div aria-hidden={'true'} className={'ar-shell is-railed sk'}>

            <header className={'ar-head'}>
                <span className={'sk-line sk-meta'} />
                <span className={'sk-line sk-title'} />
                <span className={'sk-line sk-title is-part'} />
                <span className={'sk-line sk-dek'} />
            </header>

            <aside className={'ar-rail'}>
                <div className={'ar-rail-box'}>
                    <span className={'sk-line sk-rail-title'} />
                    {RAIL_ROWS.map((width, index) => (
                        <span className={'sk-line sk-rail-row'} key={index} style={{ width: `${width}%` }} />
                    ))}
                </div>
            </aside>

            <div className={'ar-prose'}>
                <div className={'sk-body'}>
                    {PARAGRAPHS.map((widths, index) => (
                        <p className={'sk-para'} key={index}><Lines widths={widths} /></p>
                    ))}
                </div>
            </div>
        </div>
    )
}

/* The article index, before it arrives. One filter row and a few entries, which
   is all this page is. */
export const ArticlesSkeleton = ({ entries = 4 }) => {
    return (
        <div aria-hidden={'true'} className={'sk'}>
            <div className={'sk-topics'}>
                {[92, 108, 84, 96].map((width, index) => (
                    <span className={'sk-line sk-topic'} key={index} style={{ width: `${width}px` }} />
                ))}
            </div>
            <div className={'sk-entries'}>
                {Array.from({ length: entries }, (ignored, index) => (
                    <div className={'sk-entry'} key={index}>
                        <span className={'sk-line sk-entry-title'} />
                        <span className={'sk-line sk-entry-title is-part'} />
                        <span className={'sk-line sk-entry-dek'} />
                        <span className={'sk-line sk-entry-meta'} />
                    </div>
                ))}
            </div>
        </div>
    )
}

/* A document page. No header of its own, because those pages carry their title
   in the band above the fetched prose and it is already on screen. */
export const DocSkeleton = () => {
    return (
        <div aria-hidden={'true'} className={'ar-shell is-railed sk'}>
            <header className={'ar-head'}>
                <span className={'sk-line sk-dek'} />
                <span className={'sk-line sk-dek is-part'} />
            </header>
            <aside className={'ar-rail'}>
                <div className={'ar-rail-box'}>
                    <span className={'sk-line sk-rail-title'} />
                    {RAIL_ROWS.map((width, index) => (
                        <span className={'sk-line sk-rail-row'} key={index} style={{ width: `${width}%` }} />
                    ))}
                </div>
            </aside>
            <div className={'ar-prose'}>
                <div className={'sk-body'}>
                    {PARAGRAPHS.map((widths, index) => (
                        <p className={'sk-para'} key={index}><Lines widths={widths} /></p>
                    ))}
                </div>
            </div>
        </div>
    )
}
